<template>
  <div class="live-stream-container">
    <div class="stream-container">

      <div class="stream-item">
        <!-- add stream-frame to get the card look + aspect ratio -->
        <div class="frame-container stream-frame">
          <div class="frame-content">
            <div v-if="!capturedFrame || isLoadingStream" class="loading-overlay">
              <div class="loader"></div>
            </div>

            <img
              v-if="capturedFrame"
              :src="capturedFrame"
              :alt="$t('cameras.liveStream.alt')"
              ref="imageRef"
              @load="onInitialImageLoad"
            />

            <canvas id="zoneCanvas" style="position:absolute; top:0; left:0; pointer-events:none;"></canvas>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import { useRoute } from 'vue-router'

import { detection_zone_colors_by_typeid } from '@/constants.js'
import { parseStoredTripwires } from '@/utils/tripwireShape'

import { useCompanyStore } from '@/stores/CompanyStore'
import { parseDeviceUrl } from '@/api/deviceUrl'
import { useDetectionZoneStore } from '@/stores/DetectionZone'

import axios from 'axios'

export default {
    name: 'LiveStream',

    components: {
      
    },

    emits: ["streamstatusChange"],

    props: {
        streamLink: String,
        flowId: Number,
        streamId: Number,
        streamStatus: Boolean,
        socket: Object
    },

    setup(props, { emit }) {
        const socket = ref(null)

        const toast = useToast()
        const { t } = useI18n()

        const companyStore = useCompanyStore()
        const DetectionZoneStore = useDetectionZoneStore()

        const route = useRoute()

        const capturedFrame = ref(null)
        const imageRef = ref(null)

        const entranceCam = ref(false)
        const streamIsRunning = ref(false)

        const detectionZones = ref([])

        const isLoadingStream = ref(true)

        let isComponentAlive = true
        let hasJustMounted = true
        let eventSource = null

        // Create the CancelToken instance
        const source = axios.CancelToken.source()

        const startShowLocalStream = () => {
            if (props.streamLink && props.streamStatus !== null) {
                console.log('Starting local stream:', props.streamLink, props.streamStatus, props.streamId)
                socket.value.emit('start_show_live_stream_local', props.streamLink, props.streamStatus, props.streamId)
                streamIsRunning.value = true
                isLoadingStream.value = true
                setupEventSource()       
            }
        }

        const stopShowLocalStream = () => {
            if (streamIsRunning.value && props.streamLink) {
                console.log('Stopping local stream:', props.streamLink, props.streamId)
                socket.value.emit('stop_show_live_stream_local', props.streamLink, props.streamId)
                streamIsRunning.value = false
                isLoadingStream.value = true
                closeEventSource()
            }
        }

        const closeEventSource = () => {
            if (eventSource) {
                eventSource.close()
                eventSource = null
            }
        }

        const adjustCanvasSize = () => {
            const img = imageRef.value
            if (!img) return
            const canvas = document.getElementById('zoneCanvas')
            // canvas should match the visible 16:9 box size
            const w = img.clientWidth
            const h = img.clientHeight
            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w
                canvas.height = h
            }
        }

        const safeParse = (str) => {
            if (!str || typeof str !== 'string') return null
            try { return JSON.parse(str) } catch { return null }
        }

        const drawAllZones = async () => {
            if (detectionZones.value) {
                if (!isComponentAlive) return
                const canvas = document.getElementById('zoneCanvas')
                if (!canvas) return
                const ctx = canvas.getContext('2d')
                if (!ctx) return
                ctx.clearRect(0, 0, canvas.width, canvas.height)

                detectionZones.value.forEach(zone => {
                    const color = detection_zone_colors_by_typeid[zone.DetectionZoneTypeId] || 'rgba(0, 0, 0, 0.5)'

                    const polygon = safeParse(zone.DetectionZonePointArray)
                    if (Array.isArray(polygon) && polygon.length >= 3) {
                        drawPoly(polygon, color)
                    }

                    // Tripwires can now be a group (N per zone) and are stored as
                    // [{id, points}, ...]. parseStoredTripwires tolerates every
                    // historical shape and always yields that canonical form, so
                    // we can iterate and draw each one in the zone's color.
                    const tws = parseStoredTripwires(zone.DetectionZoneTripwirePoints)
                    for (const tw of tws) {
                        drawTripwire(tw.points, color)
                    }
                })
            }
        }

        const drawPoly = (points, color) => {
            const canvas = document.getElementById('zoneCanvas')
            const ctx = canvas?.getContext('2d')
            const img = imageRef.value
            if (!ctx || !img || !points?.length) return

            // Canvas is sized to the 16:9 visible box (via CSS).
            const CW = canvas.width
            const CH = canvas.height

            // Natural size of the JPEG frame (your polygon coords are in this space)
            const IW = img.naturalWidth
            const IH = img.naturalHeight
            if (!IW || !IH) return

            // “contain” fit inside the 16:9 canvas:
            // same logic the browser uses for object-fit: contain
            const scale = Math.min(CW / IW, CH / IH)
            const drawW = IW * scale
            const drawH = IH * scale
            const offsetX = (CW - drawW) / 2  // pillarbox if any
            const offsetY = (CH - drawH) / 2  // letterbox if any

            // Fill style (slightly transparent)
            const fillStyle = color.replace('0.5', '0.3')

            ctx.beginPath()
            // First point
            ctx.moveTo(points[0][0] * scale + offsetX, points[0][1] * scale + offsetY)
            // Rest of points
            for (let i = 1; i < points.length; i++) {
                const [x, y] = points[i]
                ctx.lineTo(x * scale + offsetX, y * scale + offsetY)
            }
            ctx.closePath()
            ctx.fillStyle = fillStyle
            ctx.fill()

            // Optional border that stays ~2 CSS px thick:
            // const deviceScale = Math.max(CW / drawW, CH / drawH)
            // ctx.lineWidth = Math.max(1, 2)
            // ctx.strokeStyle = '#ffffff88'
            // ctx.stroke()
        }

        const drawTripwire = (points, color) => {
            const canvas = document.getElementById('zoneCanvas')
            const ctx = canvas?.getContext('2d')
            const img = imageRef.value
            if (!ctx || !img || !points?.length || points.length !== 2) return

            const CW = canvas.width
            const CH = canvas.height
            const IW = img.naturalWidth
            const IH = img.naturalHeight
            if (!IW || !IH) return

            const scale = Math.min(CW / IW, CH / IH)
            const drawW = IW * scale
            const drawH = IH * scale
            const offsetX = (CW - drawW) / 2
            const offsetY = (CH - drawH) / 2

            const [a, b] = points
            const ax = a[0] * scale + offsetX
            const ay = a[1] * scale + offsetY
            const bx = b[0] * scale + offsetX
            const by = b[1] * scale + offsetY

            // Solid stroke for the line itself; reuse the zone-type color.
            const stroke = color.replace('0.5', '1')
            ctx.strokeStyle = stroke
            ctx.fillStyle = stroke
            ctx.lineWidth = 4
            ctx.lineCap = 'round'

            ctx.beginPath()
            ctx.moveTo(ax, ay)
            ctx.lineTo(bx, by)
            ctx.stroke()

            // Endpoint dots so the line is unambiguous.
            ctx.beginPath()
            ctx.arc(ax, ay, 5, 0, Math.PI * 2)
            ctx.fill()
            ctx.beginPath()
            ctx.arc(bx, by, 5, 0, Math.PI * 2)
            ctx.fill()
        }

        const onInitialImageLoad = () => {
            adjustCanvasSize()
            drawAllZones()
        }

        const setupEventSource = () => {
            closeEventSource() // Close any existing EventSource before creating a new one
            const { httpBase, proxied } = parseDeviceUrl(companyStore.company.SocketioDeviceURL)

            const eventSourceUrl = props.streamStatus 
                ? `${httpBase}/processed_stream/${props.streamId}` 
                : `${httpBase}/stream/${props.streamId}`
            // EventSource CANNOT set headers — the cookie is the only credential a
            // live-video request can carry, which is the whole reason known-gaps #3
            // needs one. Gated on the shape: see the withCredentials note in
            // @/api/deviceUrl, a credentialed cross-origin call would be refused.
            eventSource = new EventSource(eventSourceUrl, { withCredentials: proxied })

            eventSource.onmessage = (event) => {
                const base64Data = event.data;
                console.log(`Received frame data for stream ${props.streamId}: ${base64Data.substring(0, 30)}...`)
                const binaryString = atob(base64Data);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'image/jpeg' });
                const imageUrl = URL.createObjectURL(blob);
                capturedFrame.value = imageUrl;
                isLoadingStream.value = false;
            }

            eventSource.onerror = (error) => {
                console.error('Error with SSE:', error)
                toast.error(props.streamStatus ? t('cameras.liveStream.errorProcessed') : t('cameras.liveStream.errorLive'))
                closeEventSource()
            }
        }

        //to prevent zones are continously redrawn (flickering)
        function debounce(func, wait = 100) {
            let timeout
            return (...args) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    func.apply(this, args)
                }, wait)
            }
        }

        const handleResize = debounce(() => {
            const img = imageRef.value
            if (img) {
                adjustCanvasSize()
                drawAllZones()
            }
        }, 100)

        watch(() => isComponentAlive, (newStatus, oldStatus) => {
            if (newStatus) {
                onInitialImageLoad()
            }
        }, { immediate: true })


        onMounted(async () => {
            window.addEventListener('resize', handleResize)
            detectionZones.value = await DetectionZoneStore.fetchDetectionZonesByFlowId(parseInt(route.params.id))
            socket.value = props.socket
            console.log('started in mount')
            startShowLocalStream()
            hasJustMounted = false
        })

        watch(() => props.streamStatus, (newStatus, oldStatus) => {
            if (hasJustMounted) return
            console.log('Stream status changed:', newStatus, 'from:', oldStatus)
            
            if (newStatus !== oldStatus) {
                isLoadingStream.value = true;
                toast.info(t('cameras.liveStream.restarting'));
                console.log('stopping in watch');
                stopShowLocalStream();

                setTimeout(() => {
                    console.log('starting in watch');
                    startShowLocalStream();
                }, 3000);
            }

        }, { immediate: true })

        onBeforeUnmount(() => {
            isComponentAlive = false
            if (socket.value) {
                socket.value.off('connect')
                socket.value.off('show_live_stream_handling_processing')
                socket.value.off('local_live_stream_stopped')
                socket.value.off('error')
            }
            stopShowLocalStream()
            window.removeEventListener('resize', handleResize)
            source.cancel('Operation canceled by the user.')
            closeEventSource()
        })

        window.addEventListener('beforeunload', stopShowLocalStream)

        return {
            onInitialImageLoad,
            adjustCanvasSize,
            capturedFrame,
            imageRef,
            isLoadingStream
        }
    }
}
</script>

<style scoped>

.live-stream-container{ display:flex; flex-direction:column; }
.stream-container{ width:100%; display:flex; justify-content:space-between; }
.stream-item{ width:100%; position:relative; }

/* --- Buttons (unchanged brand colors) --- */
.btn-primary-start{ background-color:#6C9046 !important; border:none !important; }
.btn-primary-start:disabled{ background:#a9a9a9 !important; cursor:not-allowed !important; border:none !important; }
.btn-primary{ background-color:#4F7EB3 !important; border:none !important; }
.btn-primary:hover{ background-color:#B2C149 !important; }

/* --- Stream “card” --- */
.stream-frame{
  /* card visuals */
  background:#0f1113;               /* dark backdrop behind the video */
  border:1px solid #e6edf3;
  border-radius:12px;
  box-shadow:0 3px 12px rgba(13,38,76,.08);
  padding:8px;

  /* layout */
  width:100%;
}

/* keep a stable video window height; the inner content stays responsive */
.frame-content{
  position:relative;
  width:100%;
  /* 16:9 window; grows/shrinks responsively */
  aspect-ratio:16 / 9;
  overflow:hidden;
  border-radius:10px;
  background:#0b0f14;               /* fallback if no frame yet */
}

/* video image scales to fit without distortion */
.frame-content img{
  width:100%;
  height:100%;
  object-fit:contain;                /* show full frame, no crop */
  display:block;
}

/* canvas follows the visible box exactly */
#zoneCanvas{
  position:absolute;
  inset:0;                          /* top/right/bottom/left:0 */
  pointer-events:none;
  width:100%;
  height:100%;
}

/* centered overlay loader */
.loading-overlay{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  background:linear-gradient(0deg, rgba(15,17,19,0.45), rgba(15,17,19,0.45));
  z-index:2;
}

/* brand loader */
.loader{
  width:48px;
  padding:8px;
  aspect-ratio:1;
  border-radius:50%;
  background:#B2C149;
  --_m:conic-gradient(#0000 10%,#000),linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s infinite linear;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }

</style>